
import React from "react";
import { Schemes, Utils } from "../../../../../../../utils";
import { i18nManager } from "../../../../../../../i18n";
import { Elements } from "../../../../../../shared/Elements";
import { ConfirmDialog, Siren } from "../../../../../../shared/ConfirmDialog";
import * as ReactRouterDom from "react-router-dom";
import LoadingGif from "../../../../assets/images/loading_circle.gif";
import { BaseService, IMember, IProfileWebsite, IHost, MemberService, SanitizedResponse, IHostWebsite, HostService, ITournamentEdition, ITournamentEditionWebsite, TournamentService, ITournament, ICountry, CountryService } from "../../../../../../../services";
import { Button, ButtonManageRef, Checkbox, ComposedPassword, DOMHelper, Dropdown, Form, FormControl, NoseurObject, ObjectHelper, Scheme, TabPane, TabPanel, TextAreaInput, TextInput } from "@rivtn/noseur";
import ImportExport from "../../../../../../page_design/ImportExport";
import MediaInput, { MediaInputManageRef } from "../../../../../../shared/MediaInput";

export interface TourSettingsProps {
    profile: IMember;
    tournamentEdition: ITournamentEdition;
}

function TourSettings(props: TourSettingsProps) {

    const { tournamentEdition } = props;
    const countryService = CountryService.getInstance();
    const tournamentService = TournamentService.getInstance();
    const [profile, _] = React.useState<IMember>(props.profile);
    const [countries, setCountries] = React.useState<ICountry[]>();
    const [tournament, setTournament] = React.useState<any>(tournamentEdition.tournament);
    const labels = i18nManager.Labels.host.manage_tournament.settings.general.tour_settings;
    const [tournamentEditions, setTournamentEditions] = React.useState<ITournamentEdition[]>();

    React.useEffect(() => {
        countryService.queryCountries({ size: 50 }).then(({ content }: SanitizedResponse<ICountry>) => {
            setCountries(content);
        }).catch(BaseService.reportError);
        tournamentService.queryTournamentEditions({ size: 50, "tournament.external_id": tournamentEdition.tournament.external_id, __member_id__: profile.external_id }).then(({ content }: SanitizedResponse<ITournamentEdition>) => {
            setTournamentEditions(content);
        }).catch(BaseService.reportError);
    }, []);

    return (<div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
        <b>{labels.desc}</b>
        <FormControl isFieldset fill style={{ maxWidth: 600, marginTop: 15 }} label={labels.public_id} scheme={Schemes.OMNEHOST}
            rightContent={<i className="fa fa-copy" style={{ cursor: "pointer" }} onClick={() => DOMHelper.copyToClipboard(tournament.public_id, () => {
                Siren.alert(i18nManager.Labels.common.copied, { lifetime: 1000 });
            })} />}>
            <TextInput disabled defaultValue={tournament?.public_id} />
        </FormControl>
        <FormControl isFieldset fill style={{ maxWidth: 600, marginTop: 15 }} label={labels.name} scheme={Schemes.OMNEHOST}>
            <TextInput defaultValue={tournament?.name} onInputComplete={(name) => setTournament({ ...tournament, name })} />
        </FormControl>
        <FormControl isFieldset fill style={{ maxWidth: 600, marginTop: 15 }} label={labels.description} scheme={Schemes.OMNEHOST}>
            <TextAreaInput fill style={{ height: 150 }} defaultValue={tournament?.description} onInputComplete={(description) => setTournament({ ...tournament, description })} />
        </FormControl>
        <FormControl isFieldset fill style={{ maxWidth: 600, marginTop: 15 }} label={labels.op_country} scheme={Schemes.OMNEHOST}>
            <Dropdown fill options={countries} optionMap={{ label: "name", value: "code" }} selectedOptionIndex={countries?.findIndex(c => c.code === tournamentEdition.tournament?.extra_data?.country_code)}
                onSelectOption={(country: ICountry) => setTournament({ ...tournament, country_code: country.code })} />
        </FormControl>
        <FormControl isFieldset fill style={{ maxWidth: 600, marginTop: 15 }} label={labels.select_ed_logo} scheme={Schemes.OMNEHOST}>
            <Dropdown fill options={tournamentEditions} optionMap={{ label: "name", value: "external_id", icon: "logo_url" }} selectedOptionIndex={tournamentEditions?.findIndex(c => c.logo_url === tournamentEdition.tournament?.extra_data?.logo_url)}
                onSelectOption={(edition: ITournamentEdition) => setTournament({ ...tournament, logo_url: edition.logo_url })} />
        </FormControl>
        <Button scheme={Schemes.OMNEHOST} loadingProps={Elements.BUTTON_LOADING_PROPS} text={i18nManager.Labels.common.save_changes}
            style={{ marginTop: 30, padding: "10px 25px", }} onClick={(e) => updateHostSocialAccounts(e.manageRef)} />
        <div style={{ display: "flex" }}>
            <Button scheme={Scheme.DANGER} loadingProps={Elements.BUTTON_LOADING_PROPS} text={labels.close_tour} disabled={tournament.status === "CLOSED"}
                style={{ marginTop: 20, padding: "10px 25px", marginRight: 15 }} onClick={closeTournament} />
            <Button scheme={Schemes.OMNEHOST} loadingProps={Elements.BUTTON_LOADING_PROPS} text={labels.reactivate_tour} disabled={tournament.status === "ACTIVE"}
                style={{ marginTop: 20, padding: "10px 25px", }} onClick={reactivateTournament} />
        </div>
    </div>);

    function updateHostSocialAccounts(manageRef?: ButtonManageRef) {
        manageRef?.setLoadingState(true);
        tournamentService.updateTournament(tournament).then(({ sanitized }: SanitizedResponse<ITournament>) => {
            setTournament(sanitized);
            Siren.alert(labels.update_success);
        }).catch(BaseService.reportError).finally(() => manageRef?.setLoadingState(false));
    }

    function closeTournament() {
        let tournamentName = "";
        const dialogLabels = labels.dialog.close;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            desc: dialogLabels.desc.replace("${tour_name}", tournament.name),
            content: (<div style={{ margin: "10px 0px", marginTop: 20 }}>
                <a href={dialogLabels.what_happens.article_link} target="_blank">{dialogLabels.what_happens.article_label}</a>
                <p>{labels.dialog.confirm_tour.to_continue}</p>
                <FormControl style={{ marginTop: 15 }} isFieldset fill label={labels.dialog.confirm_tour.tour_name}>
                    <TextInput onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        tournamentName = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (tournamentName !== tournament.name) {
                    dialogErrorRef.current!.innerText = labels.dialog.confirm_tour.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                tournamentService.closeTournament(tournament.external_id).then(() => {
                    dialog.destroy(() => {
                        setTournament({ ...tournament, status: "CLOSED" });
                        Siren.alert(dialogLabels.success);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function reactivateTournament() {
        const dialogLabels = labels.dialog.reactivate;

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Schemes.OMNEHOST,
            desc: dialogLabels.desc.replace("${tour_name}", tournament.name),
            onConfirm: async (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                tournamentService.reactivateTournament(tournament.external_id).then(() => {
                    dialog.destroy(() => {
                        setTournament({ ...tournament, status: "ACTIVE" });
                        Siren.alert(dialogLabels.success);
                    });
                }).catch((err: any) => {
                    BaseService.reportError(err);
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default TourSettings;
